import { useEffect, useId, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { Question } from '../../config/types';
import { objectToString, stringToObject, validateField } from '../../utils/form-utils';
import { calculateAutoReportPeriod, REPORTING_PERIOD_QUARTERS } from '../../utils/reporting-period';
import Details from './Details';
import ErrorMessage from './ErrorMessage';

type Props = {
  question: Question;
  disabled?: boolean;
  anchorId?: string;
  detailsHeading?: string;
  detailsContent?: string;
};

const getYearOptions = (currentYear: number) =>
  Array.from({ length: 4 }, (_, idx) => {
    const year = currentYear - 2 + idx;
    const next = String(year + 1).slice(-2);
    return `${year}-${next}`;
  });

const parseReportPeriod = (value: string) => {
  const parsed = stringToObject(value);
  if (typeof parsed?.year === 'string' || typeof parsed?.quarter === 'string') {
    return {
      quarter: typeof parsed?.quarter === 'string' ? parsed.quarter : '',
      year: typeof parsed?.year === 'string' ? parsed.year : '',
    };
  }

  return {
    quarter: '',
    year: value.trim(),
  };
};

export function ReportPeriod({
  question,
  disabled,
  anchorId,
  detailsHeading,
  detailsContent,
}: Props) {
  const { register, getFieldState, getValues, setValue, watch } = useFormContext();
  const { error } = getFieldState(question.name);
  const id = useId();
  const baseId = anchorId ?? id;
  const isReadOnly = Boolean(disabled || question.readOnly);
  const allFields = getValues();
  const watchedFields = watch(
    question.autoFill?.field ? Object.keys(allFields).filter((key) => key !== question.name) : [],
  );

  const initialValue = useMemo(
    () => String(getValues(question.name) ?? ''),
    [getValues, question.name],
  );
  const initialParsed = useMemo(() => parseReportPeriod(initialValue), [initialValue]);

  const defaultParsed = useMemo(() => {
    if (!question.defaultValue) return { quarter: '', year: '' };
    if (
      typeof question.defaultValue === 'string' &&
      ['auto', 'auto-1'].includes(question.defaultValue.trim().toLowerCase())
    ) {
      return calculateAutoReportPeriod(question.defaultValue);
    }
    if (Array.isArray(question.defaultValue)) {
      const [quarterValue, yearValue] = question.defaultValue;
      return {
        quarter: typeof quarterValue === 'string' ? quarterValue : '',
        year: typeof yearValue === 'string' ? yearValue : '',
      };
    }
    return parseReportPeriod(String(question.defaultValue));
  }, [question.defaultValue]);

  const [quarter, setQuarter] = useState(initialParsed.quarter || defaultParsed.quarter);
  const [year, setYear] = useState(initialParsed.year || defaultParsed.year);

  const updateValue = (nextQuarter: string, nextYear: string) => {
    const combined =
      nextQuarter || nextYear ? objectToString({ quarter: nextQuarter, year: nextYear }) : '';
    setValue(question.name, combined, {
      shouldDirty: true,
      shouldValidate: false,
    });
  };

  useEffect(() => {
    if (getValues(question.name) === undefined || getValues(question.name) === '') {
      if (defaultParsed.quarter || defaultParsed.year) {
        updateValue(defaultParsed.quarter, defaultParsed.year);
        setQuarter(defaultParsed.quarter);
        setYear(defaultParsed.year);
      } else {
        setValue(question.name, '');
      }
    }
  }, [defaultParsed.quarter, defaultParsed.year, getValues, question.name, setValue]);

  useEffect(() => {
    if (!question.autoFill?.field) return;
    const valueKey = Object.keys(allFields).find((key) =>
      key.endsWith(question.autoFill?.field || ''),
    );
    if (!valueKey) return;

    const sourceValue = getValues(valueKey);
    if (!sourceValue) return;

    const parsed = parseReportPeriod(String(sourceValue));
    if (!parsed.year) return;

    const currentValue = parseReportPeriod(String(getValues(question.name) ?? ''));
    if (currentValue.year === parsed.year && currentValue.quarter === '') return;
    if (currentValue.quarter) return;

    setYear(parsed.year);
    updateValue('', parsed.year);
  }, [allFields, getValues, question.autoFill?.field, question.name, setValue, watchedFields]);

  const yearOptions = useMemo(() => getYearOptions(new Date().getFullYear()), []);

  const { ref } = register(question.name, {
    validate: (value: string) => {
      if (!question.rules?.required) {
        return true;
      }
      const parsed = stringToObject(value);
      const quarterValue = parsed?.quarter ?? '';
      const yearValue = parsed?.year ?? '';
      const combinedValue = quarterValue && yearValue ? `${quarterValue} ${yearValue}` : '';
      return validateField(
        question.title,
        { required: true },
        combinedValue,
        question.errorMessages,
      );
    },
  });

  return (
    <>
      {error && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}

      <input type="hidden" ref={ref} name={question.name} />

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div>
          <label className="govuk-label" htmlFor={`${baseId}-quarter`}>
            Quarter
          </label>
          <select
            id={`${baseId}-quarter`}
            className="govuk-select"
            disabled={isReadOnly}
            value={quarter}
            onChange={(event) => {
              const nextQuarter = event.target.value;
              setQuarter(nextQuarter);
              updateValue(nextQuarter, year);
            }}
          >
            <option value="">Select quarter</option>
            {REPORTING_PERIOD_QUARTERS.map((q) => (
              <option key={q} value={q}>
                {q}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="govuk-label" htmlFor={`${baseId}-year`}>
            Reporting Year
          </label>
          <select
            id={`${baseId}-year`}
            className="govuk-select"
            disabled={isReadOnly}
            value={year}
            onChange={(event) => {
              const nextYear = event.target.value;
              setYear(nextYear);
              updateValue(quarter, nextYear);
            }}
          >
            <option value="">Select year</option>
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>
    </>
  );
}
